"use client";

import { useState, useEffect } from "react";
import { getCurrentUserAction } from "@/resources/actions/auth.actions";
import type { User } from "@/lib/auth";
import { Role } from "@/common/enums";

/**
 * Custom hook to get the current authenticated user in client components
 *
 * @example
 * function MyComponent() {
 *   const { user, isLoading } = useAuth();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (!user) return <div>Not authenticated</div>;
 *
 *   return <div>Welcome, {user.email}!</div>;
 * }
 */
export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getCurrentUserAction()
      .then(setUser)
      .finally(() => setIsLoading(false));
  }, []);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === Role.ADMIN || user?.role === Role.SUPER_ADMIN,
  };
}
